import { Box } from '@chakra-ui/react';
import { useEffect, useRef, useState } from 'react';
import { Document, Page } from 'react-pdf';
import PdfLoader from './PdfLoader';
import { PdfToolbar } from './PdfToolbar';

export default function PdfViewer({
  file,
  viewerHeight = '80vh'
}: {
  file: string | File;
  viewerHeight?: string;
}) {
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [scale, setScale] = useState(1);
  const [search, setSearch] = useState('');
  const [containerWidth, setContainerWidth] = useState(0);

  const containerRef = useRef<HTMLDivElement>(null);
  const pageRefs = useRef<HTMLDivElement[]>([]);
  const isProgrammaticScroll = useRef(false);
  const pendingPageRef = useRef<number | null>(null);
  const skipNextScrollSync = useRef(false);
  const scrollResetTimeoutRef = useRef<number | null>(null);

  /* Scroll to page from toolbar */
  const handlePageChange = (nextPage: number) => {
    const container = containerRef.current;
    const targetPage = Math.min(Math.max(nextPage, 1), pages || 1);
    const el = pageRefs.current[targetPage - 1];

    if (!container || !el) return;

    setPage(targetPage);
    pendingPageRef.current = targetPage;
    isProgrammaticScroll.current = true;

    container.scrollTo({
      top: el.offsetTop,
      behavior: 'smooth'
    });

    if (scrollResetTimeoutRef.current) {
      window.clearTimeout(scrollResetTimeoutRef.current);
    }

    scrollResetTimeoutRef.current = window.setTimeout(() => {
      isProgrammaticScroll.current = false;
      scrollResetTimeoutRef.current = null;
    }, 300);
  };

  /* Scroll-based page detection (zoom-safe) */
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const onScroll = () => {
      const pendingPage = pendingPageRef.current;

      if (pendingPage) {
        const pendingPageEl = pageRefs.current[pendingPage - 1];
        const targetTop = pendingPageEl?.offsetTop ?? 0;
        const hasReachedTarget = Math.abs(container.scrollTop - targetTop) <= 4;

        setPage(pendingPage);

        if (hasReachedTarget) {
          pendingPageRef.current = null;
          isProgrammaticScroll.current = false;
        }

        return;
      }

      if (isProgrammaticScroll.current) {
        isProgrammaticScroll.current = false;
      }

      const containerTop = container.scrollTop;
      const containerHeight = container.clientHeight;

      let closestPage = 0;
      let closestDistance = Infinity;

      pageRefs.current.forEach((el, index) => {
        if (!el) return;

        const pageTop = el.offsetTop;
        const pageHeight = el.offsetHeight;
        const pageCenter = pageTop + pageHeight / 2;

        const viewportCenter = containerTop + containerHeight / 2;
        const distance = Math.abs(pageCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          closestPage = index;
        }
      });

      setPage(closestPage + 1);
    };

    container.addEventListener('scroll', onScroll, { passive: true });

    if (!skipNextScrollSync.current) {
      onScroll();
    }
    skipNextScrollSync.current = false;

    return () => container.removeEventListener('scroll', onScroll);
  }, [pages, scale]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const measure = () => setContainerWidth(container.clientWidth);
    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(container);

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    return () => {
      if (scrollResetTimeoutRef.current) {
        window.clearTimeout(scrollResetTimeoutRef.current);
      }
    };
  }, []);

  function highlightText(text: string, search: string): string {
    if (!search) return text;

    const escaped = search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');

    return text.replace(
      regex,
      '<mark style="background:#ffe58f;padding:0;">$1</mark>'
    );
  }

  return (
    <Box
      border="1px solid"
      borderColor="gray.200"
      borderRadius="md"
      width="full"
      p={2}
    >
      <PdfToolbar
        page={page}
        pages={pages}
        scale={scale}
        onPageChange={handlePageChange}
        setScale={setScale}
        search={search}
        setSearch={setSearch}
      />

      <Box ref={containerRef} h={viewerHeight} overflow="auto" bg="white">
        <Document
          file={file}
          // The signed URL resolves quickly; fetching and parsing the document
          // itself is the slow part, so keep the same placeholder up for it.
          loading={<PdfLoader viewerHeight={viewerHeight} />}
          onLoadSuccess={({ numPages }) => {
            pendingPageRef.current = null;
            isProgrammaticScroll.current = false;
            skipNextScrollSync.current = true;
            pageRefs.current = [];
            setPages(numPages);
            setPage(1);
          }}
        >
          {Array.from({ length: pages }, (_, i) => (
            <Box
              key={i}
              ref={el => {
                if (el) pageRefs.current[i] = el;
              }}
              mb={8}
              display="flex"
            >
              <Box
                bg="white"
                boxShadow="0 4px 14px rgba(0,0,0,0.15)"
                borderRadius="4px"
                p={2}
                mx="auto"
                flexShrink={0}
              >
                <Page
                  pageNumber={i + 1}
                  scale={scale}
                  width={containerWidth ? containerWidth - 16 : undefined}
                  renderTextLayer
                  renderAnnotationLayer
                  customTextRenderer={(textItem: any) => {
                    const { str, transform } = textItem;

                    if (!search) return str;

                    const fontSize = transform?.[0];
                    const isRotated = transform?.[1] !== 0;

                    const watermarkWords = [
                      'CONFIDENTIAL',
                      'For Reviewer Only'
                    ];

                    const isWatermark =
                      isRotated ||
                      (fontSize && fontSize > 40) ||
                      watermarkWords.some(word =>
                        str.toUpperCase().includes(word)
                      );

                    if (isWatermark) return str;

                    return highlightText(str, search);
                  }}

                  // customTextRenderer={({ str }: { str: string }) =>
                  //   search ? highlightText(str, search) : str
                  // }
                />
              </Box>
            </Box>
          ))}
        </Document>
      </Box>
    </Box>
  );
}
